import { z } from "zod";
import { money, trimmed, uuid } from "./common";

/** Персонал, рабочие смены, зарплата, чаевые (ТЗ 09 §9.5–9.7, ТЗ 07 §7.8, BR-166…184). */

const date = z.iso.date({ error: "Некорректная дата" });
const instant = z.iso.datetime({ offset: true, error: "Некорректная дата" });
const percent = z.number().min(0, { error: "От 0 до 99.99" }).max(99.99, { error: "От 0 до 99.99" });

/** Схема оплаты (core.payroll_basic): почасовая, за смену, оклад, % личных продаж (со ступенями), ночной коэффициент. */
export const payScheme = z.strictObject({
  hourlyRate: money.optional(),
  shiftRate: money.optional(),
  monthlySalary: money.optional(),
  salesPercent: percent.optional(),
  /** Ступенчатый %: часть продаж от fromMinor — по percent (до 100 000 — 3%, выше — 5%; BR-174) */
  salesTiers: z.array(z.strictObject({ fromMinor: money, percent })).max(10).optional(),
  /** Ночные часы: доплата rate × (coef − 1), напр. 1.2 = +20% (BR-169) */
  nightCoef: z.number().min(1).max(3).optional(),
  /** Чаевые выплачиваются через зарплату */
  tipsViaPayroll: z.boolean().optional(),
});
export type PayScheme = z.infer<typeof payScheme>;
/** Ночная доплата и ступени % (KPI) — «Зарплата Pro» (addon.payroll_pro, ТЗ 03 §3.1); остальное — базовая зарплата. */
export const usesPayrollPro = (s: PayScheme | null | undefined) => !!s && ((s.nightCoef ?? 1) > 1 || !!s.salesTiers?.length);

export const positionSchema = z.strictObject({ name: trimmed(1, 80), payScheme: payScheme.default({}), sort: z.number().int().min(0).max(9999).optional() });
export const positionUpdateSchema = positionSchema.partial().extend({ version: z.number().int().min(1) });

/** Кадровые данные сотрудника (FR-EMP-001): должность, приём, своя схема оплаты (null — как у должности). */
export const employmentSchema = z.strictObject({
  positionId: uuid.nullable().optional(),
  hiredAt: date.nullable().optional(),
  payScheme: payScheme.nullable().optional(),
});
/** Увольнение (BR-180): вход блокируется, данные сохраняются. */
export const dismissSchema = z.strictObject({ date, reason: trimmed(1, 300) });

// ───────── смены ─────────

export const WORK_SHIFT_STATUSES = ["open", "closed", "auto_closed", "corrected", "approved"] as const;
/** Отметка на терминале по PIN (BR-166): приход/уход переключаются автоматически. */
export const clockPinSchema = z.strictObject({ pin: z.string().regex(/^\d{4,6}$/, { error: "PIN — 4–6 цифр" }) });
/** Ручная смена менеджером (FR-SHF-002) */
export const workShiftCreateSchema = z.strictObject({
  userId: uuid,
  clockInAt: instant,
  clockOutAt: instant.nullable().optional(),
  breakMinutes: z.number().int().min(0).max(720).optional(),
  reason: trimmed(1, 300),
}).refine((v) => !v.clockOutAt || Date.parse(v.clockOutAt) > Date.parse(v.clockInAt), { error: "Окончание позже начала", path: ["clockOutAt"] });
/** Корректировка (BR-171): причина обязательна, исходные значения сохраняются */
export const workShiftCorrectSchema = z.strictObject({
  clockInAt: instant.optional(),
  clockOutAt: instant.nullable().optional(),
  breakMinutes: z.number().int().min(0).max(720).optional(),
  reason: trimmed(1, 300),
  version: z.number().int().min(1),
});
export const workShiftsQuery = z.object({
  from: date.optional(),
  to: date.optional(),
  userId: uuid.optional(),
  status: z.enum(WORK_SHIFT_STATUSES).optional(),
});
export const staffPeriodQuery = z.object({ from: date, to: date }).refine((q) => q.to >= q.from, { error: "Конец периода раньше начала", path: ["to"] })
  .refine((q) => Date.parse(q.to) - Date.parse(q.from) <= 92 * 86_400_000, { error: "Период не больше 3 месяцев", path: ["to"] });
export const approveShiftsSchema = z.strictObject({ ids: z.array(uuid).min(1).max(500) });

// ───────── зарплата ─────────

export const ADJUSTMENT_TYPES = ["bonus", "penalty", "advance", "deduction"] as const;
/** Бонусы, штрафы и удержания — «Зарплата Pro»; аванс — в базовой. */
export const PRO_ADJUSTMENT_TYPES: readonly (typeof ADJUSTMENT_TYPES)[number][] = ["bonus", "penalty", "deduction"];
export const adjustmentSchema = z.strictObject({
  userId: uuid,
  type: z.enum(ADJUSTMENT_TYPES),
  amount: money.refine((v) => v > 0, { error: "Больше 0" }),
  reason: trimmed(1, 300),
  date,
});
export const payrollRunSchema = z.strictObject({ from: date, to: date }).refine((q) => q.to >= q.from, { error: "Конец периода раньше начала", path: ["to"] })
  .refine((q) => Date.parse(q.to) - Date.parse(q.from) <= 62 * 86_400_000, { error: "Период не больше 2 месяцев", path: ["to"] });
export const payrollPaySchema = z.strictObject({
  userId: uuid.optional(),
  method: z.enum(["cash", "bank"]),
  reference: z.string().trim().max(60).nullable().optional(),
});

// ───────── чаевые ─────────

export const TIP_MODES = ["individual", "pool_equal", "pool_hours"] as const;
/** Общий котёл чаевых — «Зарплата Pro»; «каждому свои» — базовый режим. */
export const PRO_TIP_MODES: readonly (typeof TIP_MODES)[number][] = ["pool_equal", "pool_hours"];
export const tipPoolSchema = z.strictObject({ from: date, to: date, mode: z.enum(TIP_MODES) }).refine((q) => q.to >= q.from, { error: "Конец периода раньше начала", path: ["to"] });
export const tipPayoutSchema = z.strictObject({ userIds: z.array(uuid).max(200).optional() });
